import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, 
  CheckCircle, 
  XCircle, 
  DollarSign, 
  Car, 
  FileText, 
  AlertTriangle,
  HelpCircle,
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  Loader2,
  Clock,
  Users,
  Scale,
  Banknote,
  FileCheck,
  Phone,
  Mail,
  ExternalLink
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import PortalLayout from '../components/PortalLayout';
import { formatCurrency } from '../utils/currency';
import { getUserFriendlyError } from '../utils/errorMessages';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format date as MM/DD/YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

// FAQ Accordion Item
function FAQItem({ question, answer, isOpen, onToggle }) {
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-5 text-left hover:text-[#3B5BDB] transition-colors"
      >
        <span className="font-medium text-slate-900 pr-4">{question}</span>
        <ChevronDown 
          className={`h-5 w-5 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      {isOpen && (
        <div className="pb-5 text-slate-600 leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function CoveragePage() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [openFAQ, setOpenFAQ] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = getToken();
        if (!token) {
          navigate('/portal/login');
          return;
        }

        const response = await fetch(`${API_URL}/api/portal/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
          if (response.status === 401) {
            navigate('/portal/login');
            return;
          }
          throw new Error('Failed to load coverage data');
        }

        const data = await response.json();
        setDashboardData(data);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(getUserFriendlyError(err.message));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getToken, navigate]);

  // FAQ Data
  const faqs = [
    {
      question: "What exactly is diminished value?",
      answer: "Diminished value is the difference between your vehicle's market value before an accident and its value after repairs. Even with perfect repairs, a vehicle with an accident history is worth less than an identical vehicle without one. This loss in value is called 'diminished value.'"
    },
    {
      question: "When can I file a claim?",
      answer: "You can file a claim after your vehicle has been in an accident that wasn't your fault AND has been repaired. The accident must occur during your policy period, and the at-fault party's insurance must be involved. You'll need documentation of the accident, repairs, and the diminished value assessment."
    },
    {
      question: "How is my payout calculated?",
      answer: "Your payout is based on the assessed diminished value of your vehicle, minus your deductible, up to your coverage limit. We work with certified appraisers to determine the fair diminished value. For example, if your vehicle's diminished value is $8,000 and your deductible is $500, you'd receive $7,500 (subject to your coverage limit)."
    },
    {
      question: "What if the at-fault driver doesn't have insurance?",
      answer: "Your LossPay policy covers you regardless of whether the at-fault driver has insurance. If they're uninsured, you can still file a diminished value claim with us. Your coverage protects you in these situations."
    },
    {
      question: "How long does the claims process take?",
      answer: "Most claims are processed within 30-45 days after all required documentation is submitted. This includes time for the diminished value appraisal, verification, and payment processing. You can track your claim status online at any time."
    },
    {
      question: "Can I cancel my policy and get a refund?",
      answer: "Yes, you can cancel your policy at any time. If you haven't filed a claim, you may be eligible for a prorated refund based on the unused portion of your policy term. Contact our support team to discuss your options."
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E8EEFF] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 text-[#3B5BDB] animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading your coverage details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#E8EEFF] flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl border-0 rounded-2xl">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Unable to Load Coverage</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/portal')} className="bg-[#3B5BDB] hover:bg-[#2F4BC7] rounded-xl">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const policy = dashboardData?.primary_policy || {};
  const policyholder = dashboardData?.policyholder || {};
  const vehicle = policy?.vehicle || {};
  const coverageAmount = policy?.coverage_amount || 0;
  const deductible = policy?.deductible_amount || policy?.deductible || 500;
  const effectiveDate = policy?.effective_date;
  const expirationDate = policy?.expiration_date;
  const daysRemaining = policy?.days_remaining || 0;
  const coverageLevel = policy?.coverage_level_name || 'Standard Protection';

  return (
    <PortalLayout
      pageTitle="My Coverage"
      policyNumber={policy?.policy_number}
      userName={policyholder?.first_name}
    >
      <div className="bg-gradient-to-b from-[#E8EEFF] to-white min-h-full">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-[#3B5BDB] to-[#5B7FFF] text-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 md:py-14">
            <button
              onClick={() => navigate('/portal')}
              className="inline-flex items-center gap-2 text-white/80 hover:text-white font-medium mb-6 transition-colors group"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              Back to Dashboard
            </button>

            <div className="flex items-start gap-4 mb-6">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Shield className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold mb-2">
                  Your Diminished Value Protection
                </h1>
                <p className="text-white/90 text-lg max-w-2xl">
                  Understanding your coverage in plain English
                </p>
              </div>
            </div>

            {/* Coverage Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-4 border border-white/20">
                <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Coverage Limit</p>
                <p className="text-2xl font-bold">{formatCurrency(coverageAmount)}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-4 border border-white/20">
                <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Deductible</p>
                <p className="text-2xl font-bold">{formatCurrency(deductible)}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-4 border border-white/20">
                <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Status</p>
                <p className="text-2xl font-bold flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                  Active
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-4 border border-white/20">
                <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Remaining</p>
                <p className="text-2xl font-bold">{daysRemaining} days</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
          
          {/* Your Coverage Details - MOVED TO TOP */}
          <Card className="mb-8 shadow-xl border-0 rounded-2xl" data-testid="coverage-details-card">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FileText className="h-5 w-5 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">Your Coverage Details</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Policy Info */}
                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Policy Number</p>
                    <p className="font-semibold text-slate-900">{policy?.policy_number}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Coverage Level</p>
                    <p className="font-semibold text-slate-900">{coverageLevel}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Policy Period</p>
                    <p className="font-semibold text-slate-900">
                      {formatDate(effectiveDate)} — {formatDate(expirationDate)}
                    </p>
                  </div>
                </div>

                {/* Vehicle Info */}
                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Covered Vehicle</p>
                    <p className="font-semibold text-slate-900">
                      {vehicle?.year} {vehicle?.make} {vehicle?.model}{vehicle?.trim ? ` ${vehicle.trim}` : ''}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">VIN</p>
                    <p className="font-semibold text-slate-900 font-mono text-sm">{vehicle?.vin}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Vehicle Value</p>
                    <p className="font-semibold text-slate-900">
                      {formatCurrency(vehicle?.estimated_value || policy?.vehicle_value)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Coverage Amount Breakdown */}
              <div className="mt-8 pt-8 border-t border-slate-200">
                <h4 className="font-semibold text-slate-900 mb-4">How Your Payout Works</h4>
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                  <div className="grid md:grid-cols-3 gap-6 text-center">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Your Coverage Limit</p>
                      <p className="text-3xl font-bold text-green-600">{formatCurrency(coverageAmount)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Your Deductible</p>
                      <p className="text-3xl font-bold text-slate-900">{formatCurrency(deductible)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Maximum Payout</p>
                      <p className="text-3xl font-bold text-[#3B5BDB]">{formatCurrency(coverageAmount - deductible)}</p>
                    </div>
                  </div>
                  <p className="text-center text-sm text-slate-600 mt-4">
                    Your payout = Assessed Diminished Value - Deductible (up to your coverage limit)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* What is DVP - Plain English */}
          <Card className="mb-8 shadow-xl border-0 rounded-2xl overflow-hidden" data-testid="what-is-dvp-card">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <HelpCircle className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">
                    What Does Your Policy Cover?
                  </h2>
                  <p className="text-slate-600 text-lg leading-relaxed">
                    In simple terms, your LossPay policy protects you from losing money when someone else damages your car.
                  </p>
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-6 mb-6">
                <p className="text-slate-700 leading-relaxed">
                  <strong>Here's the problem we solve:</strong> Even after your car is perfectly repaired following an accident, it's worth less than before. 
                  Why? Because buyers pay less for cars with accident history — even if the repairs are flawless. 
                  This loss in value is called <span className="text-[#3B5BDB] font-semibold">"diminished value"</span> and can be thousands of dollars.
                </p>
              </div>

              <p className="text-slate-600 leading-relaxed">
                <strong>Your LossPay policy</strong> pays you up to <span className="font-bold text-[#3B5BDB]">{formatCurrency(coverageAmount)}</span> to 
                cover this loss when another driver is at fault — so you're not left paying for someone else's mistake.
              </p>
            </CardContent>
          </Card>

          {/* What's Covered vs Not Covered */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* What's Covered */}
            <Card className="shadow-lg border-0 rounded-2xl overflow-hidden border-l-4 border-l-green-500" data-testid="whats-covered-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">What's Covered</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    { icon: Car, text: "Diminished value from accidents caused by other drivers" },
                    { icon: Scale, text: "Third-party claims when at-fault insurance doesn't fully compensate" },
                    { icon: Users, text: "Accidents with uninsured or underinsured motorists" },
                    { icon: FileCheck, text: "Professional appraisal to assess your vehicle's lost value" },
                    { icon: Banknote, text: "Direct payment to you, up to your coverage limit" }
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <item.icon className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-slate-700">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* What's NOT Covered */}
            <Card className="shadow-lg border-0 rounded-2xl overflow-hidden border-l-4 border-l-red-500" data-testid="whats-not-covered-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <XCircle className="h-5 w-5 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">What's NOT Covered</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    "Accidents where you're at fault",
                    "Mechanical breakdowns or normal wear and tear",
                    "Pre-existing damage before policy purchase",
                    "Accidents that occur outside your policy period",
                    "Cosmetic damage that doesn't affect value"
                  ].map((text, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <XCircle className="h-4 w-4 text-red-500" />
                      </div>
                      <span className="text-slate-700">{text}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* How to File a Claim */}
          <Card className="mb-8 shadow-xl border-0 rounded-2xl bg-gradient-to-r from-slate-900 to-slate-800 text-white" data-testid="file-claim-cta">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-[#3B5BDB] rounded-xl flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="h-7 w-7" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Been in an Accident?</h3>
                    <p className="text-slate-300 max-w-lg">
                      If your vehicle was damaged by another driver, you may be eligible to file a diminished value claim. 
                      Our simple process guides you through every step.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => navigate('/claims/file')}
                    className="bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white rounded-xl font-semibold"
                    data-testid="file-claim-btn"
                  >
                    File a Claim
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/claims')}
                    className="border-white/30 text-white hover:bg-white/10 rounded-xl"
                    data-testid="track-claim-btn"
                  >
                    Track Existing Claim
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* FAQ Section */}
          <Card className="mb-8 shadow-lg border-0 rounded-2xl" data-testid="faq-section">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <HelpCircle className="h-5 w-5 text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">Frequently Asked Questions</h3>
              </div>

              <div className="divide-y divide-slate-200">
                {faqs.map((faq, idx) => (
                  <FAQItem
                    key={idx}
                    question={faq.question}
                    answer={faq.answer}
                    isOpen={openFAQ === idx}
                    onToggle={() => setOpenFAQ(openFAQ === idx ? null : idx)}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Need Help */}
          <Card className="shadow-lg border-0 rounded-2xl" data-testid="need-help-card">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Still Have Questions?</h3>
                  <p className="text-slate-600">
                    Our policyholder support team is here to help you understand your coverage.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a 
                    href="tel:1-833-LOSSPAY"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-slate-100 hover:bg-slate-200 rounded-xl font-medium text-slate-700 transition-colors"
                  >
                    <Phone className="h-5 w-5" />
                    1-833-LOSSPAY
                  </a>
                  <a 
                    href="mailto:support@losspay.com"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-slate-100 hover:bg-slate-200 rounded-xl font-medium text-slate-700 transition-colors"
                  >
                    <Mail className="h-5 w-5" />
                    support@losspay.com
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </PortalLayout>
  );
}
